import React, { useState, useEffect } from "react";
import { Box, Button, Grid, Typography, TextField, FormControlLabel, Checkbox, FormGroup, CircularProgress, Paper } from "@mui/material";
import { Formik, Form, FastField } from "formik";
import * as Yup from "yup";
import { useCompany } from "../../contexts/CompanyContext";
import { useUser } from "../../context/UserContext.js";
import { useSnack } from "../../services/SnakbarProvider";
import { getUserWarehousesByUserId } from "../../api/userWarehouses/index.js";
import { generateInventoryTags, reprintInventoryTags } from "../../api/inventoryTags/index.ts";

const validationSchema = Yup.object().shape({
  warehouseIds: Yup.array()
    .of(Yup.number())
    .min(1, "Seleccione al menos un almacén")
    .required("Seleccione al menos un almacén"),
  emptyTagsCount: Yup.number()
    .min(0, "El número de marbetes vacíos debe ser mayor o igual a 0")
    .required("El número de marbetes vacíos es requerido"),
});

const reprintValidationSchema = Yup.object().shape({
  codes: Yup.string()
    .required("Ingrese al menos un código de marbete")
    .test("non-empty-codes", "Ingrese al menos un código de marbete", (value) => {
      if (!value) return false;
      const codes = value.split(/[,\n]/).map((c) => c.trim()).filter((c) => c.length > 0);
      return codes.length > 0;
    }),
});

const InventoryTagForm: React.FC = () => {
  const { selectedCompany } = useCompany();
  const { userData } = useUser();
  const { openSnack } = useSnack();
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reprintSubmitting, setReprintSubmitting] = useState(false);

  useEffect(() => {
    const fetchWarehouses = async () => {
      if (!userData?.id) return;
      
      setLoading(true);
      try {
        const response = await getUserWarehousesByUserId(userData.id);
        setWarehouses(response.data || []);
      } catch (error) {
        openSnack("Error al cargar los almacenes", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchWarehouses();
  }, [userData?.id, openSnack]);

  const handleSubmit = async (values: { warehouseIds: number[]; emptyTagsCount: number }) => {
    setSubmitting(true);
    try {
      const response = await generateInventoryTags({
        warehouseIds: values.warehouseIds,
        emptyTagsCount: values.emptyTagsCount,
      });

      // Handle PDF download
      const contentDisposition = response.headers["content-disposition"];
      let fileName = "inventory_tags.pdf";

      if (contentDisposition && contentDisposition.indexOf("attachment") !== -1) {
        const matches = contentDisposition.match(/filename="([^"]*)"/);
        if (matches && matches[1]) {
          fileName = matches[1];
        }
      }

      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/pdf",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      openSnack("Marbetes generados correctamente", "success");
    } catch (error: any) {
      console.error("Error generating inventory tags:", error);
      const errorMessage = error.response?.data?.message || "Error al generar los marbetes";
      openSnack(errorMessage, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReprint = async (values: { codes: string }) => {
    setReprintSubmitting(true);
    try {
      // Parse codes from text input (comma or newline separated)
      const codes = values.codes
        .split(/[,\n]/)
        .map((c) => c.trim())
        .filter((c) => c.length > 0);

      if (codes.length === 0) {
        openSnack("Ingrese al menos un código de marbete", "error");
        return;
      }

      const response = await reprintInventoryTags({ codes });

      // Handle PDF download
      const contentDisposition = response.headers["content-disposition"];
      let fileName = "inventory_tags_reprint.pdf";

      if (contentDisposition && contentDisposition.indexOf("attachment") !== -1) {
        const matches = contentDisposition.match(/filename="([^"]*)"/);
        if (matches && matches[1]) {
          fileName = matches[1];
        }
      }

      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/pdf",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      openSnack("Marbetes reimpresos correctamente", "success");
    } catch (error: any) {
      console.error("Error reprinting inventory tags:", error);
      const errorMessage = error.response?.data?.message || "Error al reimprimir los marbetes";
      openSnack(errorMessage, "error");
    } finally {
      setReprintSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Generar Marbetes de Inventario
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Seleccione los almacenes y especifique la cantidad de marbetes vacíos a incluir.
        </Typography>

        <Formik
          initialValues={{
            warehouseIds: [] as number[],
            emptyTagsCount: 0,
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, errors, touched }) => (
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Almacenes *
                  </Typography>
                  <FormGroup>
                    {warehouses.length === 0 ? (
                      <Typography color="text.secondary">
                        No hay almacenes disponibles
                      </Typography>
                    ) : (
                      warehouses.map((warehouseAssignment) => {
                        const warehouse = warehouseAssignment.warehouse;
                        const warehouseId = warehouse?.id;
                        const warehouseName = warehouse?.name || "Sin nombre";
                        
                        if (!warehouseId) return null;

                        return (
                          <FormControlLabel
                            key={warehouseId}
                            control={
                              <Checkbox
                                checked={values.warehouseIds.includes(warehouseId)}
                                onChange={(e) => {
                                  const isChecked = e.target.checked;
                                  const currentIds = values.warehouseIds || [];

                                  if (isChecked) {
                                    setFieldValue("warehouseIds", [...currentIds, warehouseId]);
                                  } else {
                                    setFieldValue(
                                      "warehouseIds",
                                      currentIds.filter((id) => id !== warehouseId)
                                    );
                                  }
                                }}
                              />
                            }
                            label={warehouseName}
                          />
                        );
                      })
                    )}
                  </FormGroup>
                  {touched.warehouseIds && errors.warehouseIds && (
                    <Typography color="error" variant="caption" sx={{ mt: 1, display: "block" }}>
                      {errors.warehouseIds}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <FastField
                    name="emptyTagsCount"
                    as={TextField}
                    label="Cantidad de Marbetes Vacíos"
                    type="number"
                    variant="outlined"
                    fullWidth
                    inputProps={{ min: 0, step: 1 }}
                    error={touched.emptyTagsCount && Boolean(errors.emptyTagsCount)}
                    helperText={touched.emptyTagsCount && errors.emptyTagsCount}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={submitting}
                      sx={{ minWidth: 150 }}
                    >
                      {submitting ? (
                        <>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Generando...
                        </>
                      ) : (
                        "Generar Marbetes"
                      )}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Paper>

      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Reimprimir Marbetes por Código
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Ingrese los códigos de los marbetes que desea reimprimir (separados por comas o saltos de línea).
        </Typography>

        <Formik
          initialValues={{
            codes: "",
          }}
          validationSchema={reprintValidationSchema}
          onSubmit={handleReprint}
        >
          {({ values, setFieldValue, errors, touched }) => (
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FastField
                    name="codes"
                    as={TextField}
                    label="Códigos de Marbetes"
                    placeholder="Ej: 24-0001, 24-0002, 24-0003"
                    multiline
                    rows={4}
                    variant="outlined"
                    fullWidth
                    value={values.codes}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setFieldValue("codes", e.target.value);
                    }}
                    error={touched.codes && Boolean(errors.codes)}
                    helperText={
                      touched.codes && errors.codes
                        ? errors.codes
                        : "Ingrese los códigos separados por comas o saltos de línea"
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="secondary"
                      disabled={reprintSubmitting}
                      sx={{ minWidth: 150 }}
                    >
                      {reprintSubmitting ? (
                        <>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Reimprimiendo...
                        </>
                      ) : (
                        "Reimprimir Marbetes"
                      )}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Paper>
    </Box>
  );
};

export default InventoryTagForm;

